// @flow
/* eslint-disable no-unused-vars, no-unused-expressions, no-sequences, no-shadow */

import React from 'react';
import _ from 'lodash';
import bemify from 'bemify-js';

import { serviceUtils } from '@zapier/common-utils';

import Icon from 'app/common/components/Icon';
import LoadingChoice from 'app/common/components/LoadingChoice';
import SelectBox from 'app/common/components/SelectBox';
import fuzzysearch from 'app/common/fuzzysearch';
import { groupAndSortChoices } from 'app/common/utils/FormaticUtils';
import { isListChild, superHumanize } from 'app/common/CommonUtils';

const formaticPlugin = function(config: { [string]: any }) {
  // Define Zapier Types that should really just be glorified inputs
  config.alias_Int = 'PrettyText';
  config.alias_Float = 'PrettyText';
  config.alias_Datetime = 'PrettyText';
  config.alias_File = 'PrettyText';
  config.alias_Decimal = 'PrettyText';

  config.alias_Bool = 'PrettyBoolean';

  // disable css transitions until we resolve that weird bug
  config.cssTransitionWrapper = function(...children) {
    return <span>{children}</span>;
  };

  const origSingleLineString = config.alias_SingleLineString;
  config.alias_SingleLineString = function(fieldTemplate) {
    const typeName = origSingleLineString(fieldTemplate);
    if (typeName === 'Select') {
      return 'PrettySelect';
    }
    if (fieldTemplate.prefill) {
      return 'PrettySelect';
    }
    return typeName;
  };

  const origCreateElement = config.createElement;
  const addNameToClasses = function(name, props, children) {
    const { field } = props;
    const fieldType = (field || {}).type;
    if (
      name === 'field' &&
      (fieldType === 'fields' || fieldType === 'grouped-fields')
    ) {
      return;
    } // don't add .field to .fields wrappers
    props.classes = _.extend({}, props.classes, {
      [`fm-${name.toLowerCase()}`]: true,
      // Zap Templates can have overridden help text.
      'fm-help-overridden':
        name === 'help' && field && field.hasOverriddenHelpText,
    });

    if (name === 'field' && field.isCustomValue) {
      const className = isListChild(field)
        ? 'fm-custom-list-child-value'
        : 'fm-custom-value';
      props.classes[className] = true;
    }
  };
  config.createElement = function(name, props, children) {
    addNameToClasses(name, props, children);
    if (name === 'field' && props.field.type) {
      props.classes = _.extend({}, props.classes, {
        [`fm-field-type-${props.field.type}`]: true,
      });
      if (props.field.type === 'pretty-text') {
        if (!config.fieldIsSingleLine(props.field)) {
          props.classes['fm-field-type-text'] = true;
        }
      }
    }
    if (name === 'remove-item') {
      props.label = '-';
    } else if (name === 'add-item') {
      props.label = '+';
    } else if (name === 'move-item-forward') {
      props.label = 'd';
    } else if (name === 'move-item-back') {
      props.label = 'u';
    }
    return origCreateElement.call(config, name, props, children);
  };

  const origCreateElement_PrettySelectInput =
    config.createElement_PrettySelectInput;

  config.createElement_PrettySelectInput = props => {
    const { field } = props;
    // `inputType` property added by setFieldToCheckboxSelect below.
    if (field.inputType === 'checkbox') {
      return <SelectBox {...props} />;
    }
    return origCreateElement_PrettySelectInput(props);
  };

  config.renderComponent_AssocListItemKey = function(component) {
    const classes = _.clone(component.props.classes);
    return (
      <div className={bemify('', classes)}>{component.renderDefault()}</div>
    );
  };

  const DateTimeIcons = ({ field }) => (
    <span className="type-of type-of-datetime">
      <a
        href="/help/create/basics/understand-different-field-types-in-zaps#step-1"
        tabIndex="-1"
        target="_blank"
        title="Datetime field. Values will be converted into a datetime. Also allowed: human-readable dates, like '5pm tomorrow' or 'next friday at noon'; or append ' +1h' or ' -2h' to modify datetimes. Be sure to set your timezone settings! Click for more details."
      >
        <Icon className="type-of__icon" type="calendar" />
        <Icon className="type-of__icon" type="clock" />
      </a>
    </span>
  );

  // Give extra help text to the label of some types of fields
  config.renderComponent_Label = function(component) {
    let element = component.renderDefault();
    element = {
      ...element,
      props: {
        ...element.props,
        children: [...element.props.children],
      },
    };
    const field = component.props.field;
    if (field.zapierType === 'int') {
      element.props.children.push(
        <span className="type-of type-of-int" key="extra-help">
          <a
            href="/help/create/basics/understand-different-field-types-in-zaps#step-2"
            tabIndex="-1"
            target="_blank"
            title="Number field. Values will be converted into a number. Click for more details."
          >
            1 2 3
          </a>
        </span>
      );
    } else if (field.zapierType === 'float' || field.zapierType === 'decimal') {
      element.props.children.push(
        <span className="type-of type-of-float" key="extra-help">
          <a
            href="/help/create/basics/understand-different-field-types-in-zaps#step-5"
            tabIndex="-1"
            target="_blank"
            title="Decimal field. Values will be converted into a decimal or float. Click for more details."
          >
            1.0
          </a>
        </span>
      );
    } else if (field.zapierType === 'datetime') {
      element.props.children.push(
        <DateTimeIcons field={field} key="extra-help" />
      );
    } else if (field.zapierType === 'bool') {
      element.props.children.push(
        <span className="type-of type-of-bool" key="extra-help">
          <a
            href="/help/create/basics/understand-different-field-types-in-zaps#step-3"
            tabIndex="-1"
            target="_blank"
            title="True/false field. Values will be converted into 'true' ('yes', 't', or '1') or 'false' ('no', 'f' or '0'). Click for more details."
          >
            <Icon className="type-of__icon" type="disc" />
            <Icon className="type-of__icon" type="circle" />
          </a>
        </span>
      );
    } else if (field.zapierType === 'file') {
      element.props.children.push(
        <span className="type-of type-of-file" key="extra-help">
          <a
            href="/help/create/basics/understand-different-field-types-in-zaps#step-4"
            tabIndex="-1"
            target="_blank"
            title="File field. URLs, files or strings will be automatically converted into a file. Click for more details."
          >
            <Icon className="type-of__icon" type="article" />
          </a>
        </span>
      );
    } else if (field.shouldHideLabel === true || !field.label) {
      return <span key="extra-help" />;
    }
    return element;
  };

  // Improve choice handling for `|` delimited strings
  const origNormalizeChoices = config.normalizeChoices;
  config.normalizeChoices = function(choices) {
    if (_.isString(choices)) {
      choices = choices.split(',');
      choices = choices.map(choice => {
        if (choice.indexOf('|') > 0) {
          const splitted = choice.split('|');
          return {
            value: splitted[0],
            label: splitted[1],
          };
        }
        return choice;
      });
    }
    return origNormalizeChoices(choices);
  };

  // `InsertButton` is only used in flow, so it's configured there.
  config.renderComponent_InsertButton = () => null;

  config.createElement_LoadingChoice = React.createFactory(LoadingChoice);

  const actionClasses = {
    'clear-current-choice': { icon: 'clear', className: 'choice-other-action' },
    'enter-custom-value': {
      icon: 'custom-value',
      className: 'choice-other-action',
    },
    'empty-choices': { icon: 'article', className: 'choice-other-action' },
    'load-external-link': { icon: 'article', className: 'choice-other-action' },
    'load-more-choices': { icon: 'reload', className: 'choice-other-action' },
    'insert-field': { icon: 'edit', className: 'choice-action' },
  };

  config.createElement_ChoiceActionSample = function(props) {
    const config = actionClasses[props.action] || {
      className: 'choice-action',
      icon: 'edit',
    };
    return <Icon className={config.className} type={config.icon} />;
  };

  const origFieldLabel = config.fieldLabel;
  config.fieldLabel = function(field) {
    if (!field.label) {
      return superHumanize(field.key);
    }
    return origFieldLabel(field);
  };

  config.renderComponent_ChoicesSearch = component => {
    const element = component.renderDefault();
    return React.cloneElement(
      element,
      {},
      <div>
        <Icon className="choices-search__search-icon" type="search" />
        {element.props.children}
      </div>
    );
  };

  config.createElement_ChoiceSectionHeader = props => {
    const choice = props.choice;
    // Folders don't have `selectedApi` so `||` to handle them
    const selectedApi = serviceUtils.stripVersion(choice.selectedApi || '');
    const logoClass = selectedApi ? `${selectedApi}16x16` : null;
    const logoClasses = bemify('', 'img16', 'PlaceholderAPI16x16', logoClass);
    const choiceRowClasses = bemify('', 'choice-row', {
      'choice-section-disabled': props.isDisabled,
    });

    const arrow = (
      <span className="choice-section-arrow">
        {props.isDisabled ? null : (
          <Icon type={props.isOpen ? 'arrow-up' : 'arrow-down'} />
        )}
      </span>
    );

    // Used in http://localhost:8000/app/contact-us
    if (choice.isFolder) {
      const folderIcon = Number(choice.sectionKey) === 0 ? 'bolt' : 'folder';
      return (
        <div className={choiceRowClasses}>
          <Icon type={folderIcon} />
          <span className="choice-name">{choice.label}</span>
          {arrow}
        </div>
      );
    } else if (choice.stepNumber) {
      const stepNumberClasses = bemify('choice-step-number')({
        '--path-step': choice.isStepInPath,
      });

      const backgroundColor = choice.isStepInPath
        ? choice.lightPathColor
        : undefined;

      return (
        <div className={choiceRowClasses}>
          <span className={stepNumberClasses} style={{ backgroundColor }}>
            {choice.visibleStepNumber}
          </span>
          <span className={bemify('', logoClasses)} />
          <span className="choice-name">{choice.label}</span>
          {arrow}
        </div>
      );
    } else {
      const fancyIcons = {
        'empty-choices-section': 'alert',
      };
      const icon = fancyIcons[choice.sectionKey] || 'info';

      return (
        <div className={`choice-section-header ${choice.sectionKey}`}>
          <Icon className="choice-fancy-header" type={icon} />
          <span className="choice-name">{choice.label}</span>
          {arrow}
        </div>
      );
    }
  };

  config.isSearchStringInChoice = (searchString, choice) => {
    return (
      (!!choice.label && fuzzysearch(searchString, choice.label)) ||
      (!!choice.sample && fuzzysearch(searchString, choice.sample)) ||
      choice.value === '///loading///' ||
      !!choice.shouldShowInSearch
    );
  };

  config.sortChoices = (choices, searchString) => {
    return searchString ? groupAndSortChoices(choices, searchString) : choices;
  };
};

// Mark a field as a checkbox select field so we can render it differently than
// a normal select field. See createElement_PrettySelectInput above.
export const setFieldToCheckboxSelect = (fieldTemplate: Object) => ({
  ...fieldTemplate,
  inputType: 'checkbox',
});

export default formaticPlugin;
